import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { motion, animate } from "framer-motion";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid,
  AreaChart, Area, ReferenceDot,
} from "recharts";
import axios from "axios";
import { CustomerSignaturePad } from "../components/CustomerSignaturePad";
import { formatMoney } from "../lib/money";
import { fileUrl } from "../lib/api";
import { LOGO_URL, currency, QUOTE_TYPES } from "../lib/constants";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { PostSignDepositDialog } from "../components/PostSignDepositDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GKEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const PANEL_MM = { w: 1762, h: 1134 };
const metersPerPx = (lat, zoom) => 156543.03392 * Math.cos((lat * Math.PI) / 180) / Math.pow(2, zoom);

const IMG = {
  house: "/brand/house.png",
  plant: "/brand/proposal/plant.jpg",
  leaves: "/brand/proposal/leaves.jpg",
  hero: "/brand/proposal/hero.jpg",
};

export default function PublicQuote() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [sel, setSel] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [showAccept, setShowAccept] = useState(false);
  const [acceptIdx, setAcceptIdx] = useState(null);
  const [paying, setPaying] = useState(false);
  const [payPoll, setPayPoll] = useState(false);
  const [depositPrompt, setDepositPrompt] = useState(null);
  const [paymentError, setPaymentError] = useState("");
  const payBusy = useRef(false);
  const pageRef = useRef();

  const load = () => {
    const params = new URLSearchParams(window.location.search);
    const isPreview = params.get("preview") === "1" || params.has("print");
    return axios.get(`${API}/public/quote/${token}${isPreview ? "?preview=1" : ""}`).then((r) => {
      setData(r.data);
      if (r.data.quote.signed) {
        const index = r.data.option_calcs.findIndex(o => o.option_id === r.data.quote.selected_option_id);
        if (index >= 0) setSel(index);
      }
      return r.data;
    }).catch(() => setErr(true));
  };
  useEffect(() => { load(); }, [token]);
  useEffect(() => { if (data?.quote) document.title = `Solar Proposal ${data.quote.quote_no} · Grian Solar Power`; }, [data]);

  const payDeposit = async () => {
    if (payBusy.current) return;
    payBusy.current = true;
    setPaying(true); setPaymentError("");
    try {
      const { data: r } = await axios.post(`${API}/public/quote/${token}/pay-deposit`, { origin_url: window.location.origin });
      window.location.href = r.checkout_url;
    } catch (e) {
      const message = e?.response?.data?.detail || "Could not start the payment — please try again";
      setPaymentError(message); toast.error(message); setPaying(false); payBusy.current = false;
    }
  };

  // handle return from Stripe checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (params.get("payment") === "success" && sid) {
      setPayPoll(true);
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const { data: s } = await axios.get(`${API}/payments/status/${sid}`);
          if (s.payment_status === "paid") {
            toast.success("Deposit payment received — thank you!");
            setPayPoll(false); load();
            window.history.replaceState({}, "", window.location.pathname);
            return;
          }
          if (["failed", "expired"].includes(s.payment_status)) {
            toast.error("Payment was not completed — you can try again anytime.");
            setPayPoll(false);
            window.history.replaceState({}, "", window.location.pathname);
            return;
          }
        } catch { /* transient */ }
        if (tries < 10) setTimeout(poll, 2000);
        else { setPayPoll(false); toast.info("Payment is still processing — we'll confirm it shortly."); }
      };
      poll();
    } else if (params.get("payment") === "cancelled") {
      toast.info("Payment cancelled — you can pay your deposit anytime.");
      window.history.replaceState({}, "", window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ?pay=1 deep link (from the signed-confirmation email)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("pay") === "1" && data?.quote?.signed && !data.quote.deposit_paid) {
      window.history.replaceState({}, "", window.location.pathname);
      payDeposit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.quote?.signed]);

  if (err) return <div data-testid="proposal-load-error" className="min-h-screen flex items-center justify-center text-slate-400">Quote not found.</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-400"><Icons.Loader2 className="animate-spin mr-2" /> Loading your proposal…</div>;

  const { quote, lead } = data;
  const options = data.option_calcs || [];
  if (!options.length) return <div data-testid="proposal-load-error" className="min-h-screen flex items-center justify-center text-slate-400">Proposal has no options yet.</div>;
  const S = options[0].settings || {};
  const company = S.company || {};
  const reviews = S.reviews || {};
  const about = S.about_us || {};
  const accreditations = S.accreditations || [];
  const address = lead?.full_address || [lead?.street, lead?.suburb, lead?.state, lead?.postcode].filter(Boolean).join(", ");

  const upload = async (file) => {
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try { await axios.post(`${API}/public/quote/${token}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${file.name}`); load(); } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const signedCalc = options.find((o) => o.option_id === quote.selected_option_id) || options[0];
  const depositDue = quote.signed ? (signedCalc?.deposit || 0) : 0;
  const quoteForOption = (idx) => quote.signed && options[idx]?.option_id !== quote.selected_option_id
    ? { ...quote, signed: false, signed_by: null, signed_at: null, signature_image: null, signature_date: null, selected_option_label: null }
    : quote;

  // The visitor's page is never used for capture; Chromium opens a separate print-only view.
  const renderProposal = (optIdxs, forPdf, ref) => {
  return (
    <div className={`pq-canvas text-slate-900 min-h-screen font-body ${forPdf ? "pdf-stage-inner" : ""}`} ref={ref} data-testid={forPdf ? "pdf-stage" : "proposal-page"} data-pdf-state="idle">

      {payPoll && (
        <div className="no-print fixed top-0 inset-x-0 z-[65] bg-emerald-600 text-white text-sm font-semibold text-center py-2.5 flex items-center justify-center gap-2" data-testid="payment-confirming-banner">
          <Icons.Loader2 size={15} className="animate-spin" /> Confirming your payment…
        </div>
      )}

      {/* Top bar */}
      <div className="no-print sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-slate-100 flex flex-wrap gap-2 items-center justify-between px-5 sm:px-8 py-3">
        <div className="flex items-center gap-2 sm:gap-3">
          <button onClick={() => { try { window.close(); } catch {} setTimeout(() => { if (!window.closed && window.history.length > 1) window.history.back(); }, 150); }} data-testid="back-close-btn"
            className="text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-full p-2 transition-colors" title="Go back / close">
            <Icons.ArrowLeft size={18} />
          </button>
          <img src={LOGO_URL} alt="Grian Solar Power" className="h-8 sm:h-9" />
        </div>
        <div className="flex flex-wrap gap-2 items-center justify-end max-w-full">
          {!quote.signed && <button onClick={() => setShowAccept(true)} data-testid="open-accept"
            className="text-sm px-4 py-2 rounded-full bg-emerald-600 text-white font-semibold hover:bg-emerald-700 transition-colors flex items-center gap-1.5"><Icons.PenLine size={15} /> Accept &amp; Sign</button>}
          {quote.signed && depositDue > 0 && !quote.deposit_paid && (
            <button onClick={payDeposit} disabled={paying} data-testid="pay-deposit-top"
              className="text-sm px-4 py-2 rounded-full bg-emerald-600 text-white font-semibold hover:bg-emerald-700 transition-colors flex items-center gap-1.5 disabled:opacity-60">
              {paying ? <><Icons.Loader2 size={15} className="animate-spin" /> Redirecting…</> : <><Icons.CreditCard size={15} /> Pay {formatMoney(depositDue)} deposit</>}
            </button>
          )}
        </div>
      </div>

      {/* HERO — also the PDF's first page */}
      <div className={`pq-hero relative flex items-end overflow-hidden ${forPdf ? "h-[1240px]" : "min-h-[72vh]"}`}>
        <img src={IMG.hero} alt="Solar home" crossOrigin="anonymous" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-900/45 to-slate-900/20" />
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
          className="relative max-w-6xl mx-auto px-6 sm:px-10 pb-14 w-full text-white">
          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-500/20 border border-emerald-300/30 text-emerald-100 px-3 py-1 text-xs font-semibold tracking-wide mb-5">
            <Icons.Sun size={13} /> Solar Proposal · {quote.quote_no}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] max-w-3xl">Your clean-energy future starts here</h1>
          {/* Company details */}
          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-white/85 text-sm">
            <span className="font-bold text-white text-base tracking-tight">{company.name || "Grian Solar Power"}</span>
            <span className="inline-flex items-center gap-1.5"><Icons.BadgeCheck size={15} className="text-emerald-300" /> ABN {company.abn || "91 635 362 560"}</span>
            <span className="inline-flex items-center gap-1.5"><Icons.Globe size={15} className="text-emerald-300" /> {company.website || "griansolarpower.com.au"}</span>
          </div>
          <p className="mt-4 text-white/80 text-lg">Prepared for <span className="text-white font-semibold">{lead?.first_name} {lead?.last_name}</span></p>
          {address && <p className="mt-1 text-white/70 text-sm flex items-center gap-1.5"><Icons.MapPin size={14} /> {address}</p>}
          <div className="mt-1 flex flex-wrap gap-x-5 gap-y-1 text-white/70 text-sm" data-testid="hero-contact">
            {lead?.email && <span className="inline-flex items-center gap-1.5"><Icons.Mail size={14} /> {lead.email}</span>}
            {lead?.phone && <span className="inline-flex items-center gap-1.5"><Icons.Phone size={14} /> {lead.phone}</span>}
          </div>
          {quote.prepared_by?.name && (
            <div className="mt-8 inline-flex items-center gap-4 rounded-2xl bg-white/10 border border-white/15 backdrop-blur-sm px-5 py-4">
              <div className="w-11 h-11 rounded-full bg-emerald-500/90 flex items-center justify-center font-bold text-white text-lg shrink-0">{quote.prepared_by.name.trim().charAt(0).toUpperCase()}</div>
              <div className="min-w-0">
                <div className="text-white/60 text-[11px] uppercase tracking-wider">Prepared by</div>
                <div className="font-semibold text-white leading-tight">{quote.prepared_by.name}</div>
                <div className="text-white/70 text-sm flex flex-wrap gap-x-4 gap-y-0.5 mt-0.5">
                  {quote.prepared_by.phone && <span className="inline-flex items-center gap-1"><Icons.Phone size={12} /> {quote.prepared_by.phone}</span>}
                  {quote.prepared_by.email && <span className="inline-flex items-center gap-1"><Icons.Mail size={12} /> {quote.prepared_by.email}</span>}
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* ABOUT US + ACCREDITATIONS + REVIEWS */}
      <Section className="bg-white" data-testid="proposal-about-us">
        <Kicker>About Grian Solar Power</Kicker>
        <H2>Quality solar, done properly</H2>
        <p className="mt-5 text-slate-600 text-[15px] leading-relaxed whitespace-pre-line max-w-4xl">{about.intro}</p>

        {/* Accreditation logos — side by side, above the cards */}
        {accreditations.length > 0 && (
          <div className="mt-8 flex flex-wrap items-center justify-center gap-6 sm:gap-10 bg-slate-50 border border-slate-100 rounded-2xl px-6 py-6 pq-avoid-break">
            {accreditations.map((a, i) => (
              <img key={i} src={a.url} alt={a.label} title={a.label} crossOrigin="anonymous" className="h-14 sm:h-16 w-auto object-contain" data-testid={`accreditation-${i}`} />
            ))}
          </div>
        )}

        {/* About-us cards */}
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {(about.cards || []).map((c, i) => {
            const Ic = Icons[c.icon] || Icons.Sparkles;
            return (
              <div key={i} className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm pq-avoid-break">
                <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center mb-3"><Ic size={18} className="text-emerald-600" /></div>
                <div className="font-bold text-slate-900 text-sm">{c.title}</div>
                <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{c.text}</p>
              </div>
            );
          })}
        </div>

      </Section>

      {/* GOOGLE REVIEWS — own page */}
      {(reviews.rating || (reviews.cards || []).length > 0) && (
        <Section className="bg-white">
          <div className="flex flex-col items-center text-center gap-2">
            <div className="flex items-center gap-2">
              <img alt="Google" src="/brand/proposal/google.svg" className="h-7 w-7" crossOrigin="anonymous" />
              <span className="font-bold text-slate-800 text-lg">Google Reviews</span>
            </div>
            {reviews.rating && (
              <div className="flex items-center gap-2">
                <span className="text-2xl font-extrabold text-slate-900">{reviews.rating}</span>
                <Stars n={reviews.rating} />
                <span className="text-sm text-slate-500">· {reviews.count} reviews</span>
              </div>
            )}
          </div>
          {(reviews.cards || []).length > 0 && (
            <div className="mt-8 grid sm:grid-cols-3 gap-4">
              {reviews.cards.map((r, i) => (
                <div key={i} className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm pq-avoid-break">
                  <Stars n={r.rating || 5} />
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed">“{r.text}”</p>
                  <div className="text-xs font-semibold text-slate-500 mt-3">— {r.author}</div>
                </div>
              ))}
            </div>
          )}
          {(reviews.more_url || reviews.url) && (
            <div className="text-center mt-6">
              <a href={reviews.more_url || reviews.url} target="_blank" rel="noreferrer" data-testid="more-reviews"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-slate-200 font-semibold text-slate-700 hover:bg-slate-50 transition-colors text-sm">
                <Icons.Star size={15} className="text-amber-400 fill-amber-400" /> Check more reviews
              </a>
            </div>
          )}
        </Section>
      )}

      {/* SUMMARY — choose your system */}
      <Section className="bg-slate-50/70" data-testid="proposal-options-summary">
        <Kicker>Your Options</Kicker>
        <H2>Choose the system that suits you</H2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {(forPdf ? optIdxs : options.map((_, i) => i)).map((i) => (
            <SummaryCard key={options[i].option_id} c={options[i]} idx={i} active={sel === i} onSelect={() => setSel(i)} />
          ))}
        </div>
      </Section>

      {/* TABS */}
      {options.length > 1 && (
        <div className="no-print sticky top-[57px] z-30 bg-white/90 backdrop-blur border-b border-slate-100">
          <div className="max-w-6xl mx-auto px-6 sm:px-10 py-3 flex flex-wrap gap-2" data-testid="system-option-tabs">
            {options.map((c, i) => (
              <button key={c.option_id} onClick={() => setSel(i)} data-testid={`system-tab-${i}`}
                className={`max-w-full min-w-0 break-words px-4 py-2 rounded-full text-sm font-semibold transition-colors flex items-center gap-1.5 ${sel === i ? "bg-emerald-600 text-white shadow" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                System {i + 1} · {c.option_label}
                {c.recommended && <Icons.Star size={13} className={sel === i ? "text-amber-300 fill-amber-300" : "text-amber-400 fill-amber-400"} />}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* DEEP DIVE (selected option, or all when exporting) */}
      {optIdxs.map((i) => (
        <OptionDetail key={options[i].option_id} idx={i} calc={options[i]} lead={lead} quote={forPdf ? quoteForOption(i) : quote} S={S} exporting={forPdf} showHeader={forPdf}
          onAccept={() => { setAcceptIdx(i); setShowAccept(true); }} />
      ))}

      {/* UPLOAD (customer docs) */}
      <div className="no-print bg-slate-950 text-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-14">
          <div className="text-emerald-400 text-xs uppercase tracking-[0.25em] font-semibold mb-3">Next step</div>
          <div className="grid lg:grid-cols-2 gap-6 mt-2">
            <div className="bg-white text-slate-900 rounded-2xl p-6">
              {quote.signed ? (
                <div>
                  <div className="flex items-center gap-3">
                    <Icons.CheckCircle2 className="text-emerald-500 shrink-0" size={32} />
                    <div><div className="font-bold text-lg">Accepted &amp; Signed</div>
                      <div className="text-sm text-slate-500">Signed by {quote.signed_by}{quote.selected_option_label ? ` · ${quote.selected_option_label}` : ""}</div></div>
                  </div>
                  {depositDue > 0 && (
                    quote.deposit_paid ? (
                      <div className="mt-4 flex items-center gap-2 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3 text-sm font-semibold text-emerald-700" data-testid="deposit-paid-badge">
                        <Icons.BadgeCheck size={17} /> Deposit paid — {formatMoney(quote.deposit_paid_amount || depositDue)}
                      </div>
                    ) : (
                      <div className="mt-4">
                        <p className="text-sm text-slate-500 mb-2">Secure your installation date by paying your deposit online.</p>
                        <button onClick={payDeposit} disabled={paying} data-testid="pay-deposit-card"
                          className="px-6 py-3 rounded-full bg-emerald-600 text-white font-semibold hover:bg-emerald-700 transition-all inline-flex items-center gap-2 disabled:opacity-60">
                          {paying ? <><Icons.Loader2 size={16} className="animate-spin" /> Redirecting to secure payment…</> : <><Icons.CreditCard size={16} /> Pay {formatMoney(depositDue)} deposit</>}
                        </button>
                        <div className="text-xs text-slate-400 mt-2 flex items-center gap-1"><Icons.Lock size={11} /> Secure card payment powered by Stripe</div>
                      </div>
                    )
                  )}
                </div>
              ) : (
                <>
                  <h3 className="font-bold text-lg mb-1 flex items-center gap-2"><Icons.PenLine size={18} className="text-emerald-600" /> Ready to proceed?</h3>
                  <p className="text-sm text-slate-500 mb-4">Review your options above, then accept &amp; sign your chosen system.</p>
                  <button onClick={() => setShowAccept(true)} data-testid="open-accept-2"
                    className="px-7 py-3 rounded-full bg-emerald-600 text-white font-semibold hover:bg-emerald-700 transition-all">Accept &amp; Sign Proposal</button>
                </>
              )}
            </div>
            <div className="bg-white text-slate-900 rounded-2xl p-6">
              <h3 className="font-bold text-lg mb-1 flex items-center gap-2"><Icons.Upload size={18} className="text-emerald-600" /> Upload your documents</h3>
              <p className="text-sm text-slate-500 mb-4">Electricity bill, ID, etc. PDF or images.</p>
              <label className="inline-flex items-center gap-2 px-5 py-3 rounded-full border-2 border-dashed border-slate-300 cursor-pointer font-semibold text-sm hover:border-emerald-500 hover:text-emerald-600 transition-colors">
                <Icons.FilePlus size={16} /> {uploading ? "Uploading…" : "Choose file"}
                <input type="file" accept="application/pdf,image/*" className="hidden" data-testid="doc-upload"
                  onChange={(e) => e.target.files[0] && upload(e.target.files[0])} disabled={uploading} />
              </label>
              {data.documents?.length > 0 && (
                <div className="mt-4 space-y-2">
                  {data.documents.map((d) => (<div key={d.id} className="flex items-center gap-2 text-sm text-slate-600"><Icons.CheckCircle2 size={15} className="text-emerald-500" /> {d.filename}</div>))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* FOOTER / TERMS + acceptance signature */}
      <div className="pdf-break max-w-6xl mx-auto px-6 sm:px-10 py-12">
        <div className="text-xs uppercase tracking-[0.25em] font-semibold text-slate-400 mb-3">Terms &amp; Conditions</div>
        <p className="text-sm text-slate-500 max-w-4xl leading-relaxed whitespace-pre-line">{S.terms_and_conditions}</p>
        <div className="pq-avoid-break mt-10">
          <div className="text-xs uppercase tracking-[0.25em] font-semibold text-slate-400 mb-3">Acceptance of Terms &amp; Conditions</div>
          <SignatureBlock quote={forPdf && optIdxs.length === 1 ? quoteForOption(optIdxs[0]) : quote} optionLabel={forPdf && optIdxs.length === 1 ? options[optIdxs[0]]?.option_label : quote.selected_option_label} onOpen={() => setShowAccept(true)} testId="terms-signature" />
        </div>
        <div className="pq-avoid-break mt-8 flex items-center gap-3 text-sm text-slate-400 border-t border-slate-100 pt-6">
          <img src={LOGO_URL} alt="logo" className="h-8" /> © {new Date().getFullYear()} {company.name} · ABN {company.abn}
        </div>
      </div>

    </div>
  );
  };

  // ?print=all | ?print=<idx> → static print rendering used by the server-side (Chromium) vector PDF
  const printParam = new URLSearchParams(window.location.search).get("print");
  if (printParam !== null) {
    const idxs = printParam === "all" || printParam === "" ? options.map((_, i) => i) : [Math.min(options.length - 1, Math.max(0, +printParam || 0))];
    if (new URLSearchParams(window.location.search).get("kind") === "quotation") {
      return <div className="pdf-stage pdf-print" data-testid="pdf-print-root">
        {idxs.map((idx) => <div className="pdf-quotation" key={options[idx].option_id} data-testid={`quotation-sheet-${idx}`}>
          <QuoteSheet calc={options[idx]} lead={lead} quote={quote} company={company} terms={S.terms_and_conditions} />
        </div>)}
      </div>;
    }
    return <div className="pdf-stage pdf-print" data-testid="pdf-print-root">{renderProposal(idxs, true, pageRef)}</div>;
  }

  return (
    <>
      {renderProposal([sel], false, pageRef)}

      {showAccept && <AcceptModal token={token} options={options} defaultSel={acceptIdx ?? sel} S={S}
        onClose={() => { setShowAccept(false); setAcceptIdx(null); }}
        onSigned={(receipt) => {
          setShowAccept(false); setAcceptIdx(null); setPaymentError("");
          if (receipt.deposit_amount > 0 && !receipt.deposit_paid) setDepositPrompt(receipt);
          load();
        }} />}
      <PostSignDepositDialog receipt={depositPrompt} onClose={() => setDepositPrompt(null)}
        onPay={payDeposit} busy={paying} error={paymentError} />
    </>
  );
}

/* ------------- Signature block (online + PDF) ------------- */
const fmtAU = (d) => { try { return new Date(d).toLocaleDateString("en-AU", { day: "2-digit", month: "long", year: "numeric" }); } catch { return ""; } };
function SignatureBlock({ quote, optionLabel, onOpen, testId }) {
  return (
    <div className="pq-avoid-break bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-sm max-w-3xl" data-testid={testId}>
      {quote.signed ? (
        <>
          <div className="flex items-center gap-2 text-emerald-600 font-bold text-sm mb-4"><Icons.CheckCircle2 size={18} /> Accepted &amp; Signed</div>
          <div className="grid sm:grid-cols-2 gap-8">
            <div>
              {quote.signature_image ? (
                <img src={quote.signature_image} alt="Customer signature" className="h-14 object-contain" data-testid={`${testId}-image`} />
              ) : (
                <div className="h-14 flex items-end font-semibold text-slate-800 italic text-lg">{quote.signed_by}</div>
              )}
              <div className="border-t-2 border-slate-300 mt-1 pt-1.5 text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Customer Signature — {quote.signed_by}</div>
            </div>
            <div>
              <div className="h-14 flex items-end font-semibold text-slate-800">{fmtAU(quote.signature_date || quote.signed_at)}</div>
              <div className="border-t-2 border-slate-300 mt-1 pt-1.5 text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Date</div>
            </div>
          </div>
          <div className="mt-5 text-sm text-slate-700">Quote option signed: <span className="font-bold text-slate-900" data-testid={`${testId}-option`}>{quote.selected_option_label || optionLabel}</span></div>
        </>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 gap-8">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Customer Signature</div>
              <div className="border-b-2 border-slate-300 h-12 mt-6" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Date</div>
              <div className="border-b-2 border-slate-300 h-12 mt-6" />
            </div>
          </div>
          <div className="mt-5 text-sm text-slate-700">Quote option: <span className="font-bold text-slate-900" data-testid={`${testId}-option`}>{optionLabel || "________________"}</span></div>
          {onOpen && (
            <button onClick={onOpen} data-testid={`${testId}-btn`}
              className="no-print mt-6 px-6 py-2.5 rounded-full bg-emerald-600 text-white font-semibold hover:bg-emerald-700 transition-all inline-flex items-center gap-2 text-sm">
              <Icons.PenLine size={15} /> Accept &amp; Sign Online
            </button>
          )}
        </>
      )}
    </div>
  );
}

/* ------------- Quotation-only sheet (property quotation, PDF capture) ------------- */
function QuoteSheet({ calc, lead, quote, company, terms }) {
  const addr = lead?.full_address || [lead?.street, lead?.suburb, lead?.state, lead?.postcode].filter(Boolean).join(", ");
  const today = new Date().toLocaleDateString("en-AU", { day: "2-digit", month: "long", year: "numeric" });
  const perf = calc.performance || {};
  return (
    <div className="w-full bg-white text-slate-900 font-body p-10">
      {/* header */}
      <div className="flex items-start justify-between border-b-2 border-emerald-600 pb-5">
        <img src={LOGO_URL} alt="logo" className="h-12" crossOrigin="anonymous" />
        <div className="text-right">
          <div className="text-2xl font-extrabold tracking-tight text-slate-900">QUOTATION</div>
          <div className="text-sm text-slate-500 mt-1">{quote.quote_no}-{calc.option_code}</div>
          <div className="text-xs text-slate-400">{today}</div>
          {calc.expires_on && <div className="text-xs text-slate-600" data-testid={`quotation-sheet-expiry-${calc.option_id}`}>Valid until {calc.expires_on.split("-").reverse().join("/")}</div>}
        </div>
      </div>
      <div className="flex justify-between mt-2 text-xs text-slate-500">
        <span className="font-bold text-slate-700">{company.name || "Grian Solar Power"}</span>
        <span>ABN {company.abn || "91 635 362 560"}</span>
      </div>

      {/* prepared for */}
      <div className="qs-avoid-break mt-7 grid grid-cols-2 gap-5">
        <div className="border border-slate-200 rounded-xl p-5">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1">Prepared for</div>
          <div className="font-bold text-lg">{lead?.first_name} {lead?.last_name}</div>
          {addr && <div className="text-sm text-slate-500 mt-1">{addr}</div>}
          {lead?.email && <div className="text-sm text-slate-500">{lead.email}</div>}
          {lead?.phone && <div className="text-sm text-slate-500">{lead.phone}</div>}
        </div>
        <div className="border border-slate-200 rounded-xl p-5">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1">Prepared by</div>
          <div className="font-semibold">{quote.prepared_by?.name || "Grian Solar Power"}</div>
          {quote.prepared_by?.phone && <div className="text-sm text-slate-500 mt-0.5">{quote.prepared_by.phone}</div>}
          {quote.prepared_by?.email && <div className="text-sm text-slate-500">{quote.prepared_by.email}</div>}
        </div>
      </div>

      {/* site details */}
      <div className="qs-avoid-break mt-6 border border-slate-200 rounded-xl p-5">
        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-3">Site Details</div>
        <div className="grid grid-cols-3 gap-x-6 gap-y-3">
          {siteDetailPairs(lead).map(([l, v]) => (
            <div key={l}><div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{l}</div>
              <div className={`text-sm font-semibold ${v ? "text-slate-800" : "text-amber-600 italic"}`}>{v || "Pending"}</div></div>
          ))}
        </div>
      </div>

      {/* system summary */}
      <div className="qs-avoid-break mt-6 border border-slate-200 rounded-xl p-5">
        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-3">System Summary — {calc.option_label}</div>
        <table className="w-full text-sm">
          <tbody>
            {productSummary(calc).map((p, i) => (
              <tr key={i} className="border-t border-slate-100 first:border-t-0">
                <td className="py-2 font-semibold text-slate-800">{p.brand} {p.model}</td>
                <td className="py-2 text-slate-400 text-right">{p.meta}</td>
                <td className="py-2 text-right font-bold w-16">{`× ${p.qty}`}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {/* small inline stats — no big box */}
        <div className="flex flex-wrap gap-x-8 gap-y-1 mt-3 pt-3 border-t border-slate-100 text-sm">
          {calc.system_kw > 0 && <span className="text-slate-500">Solar PV: <b className="text-slate-800">{calc.system_kw} kW</b></span>}
          {calc.nominal_capacity > 0 && <span className="text-slate-500">Battery: <b className="text-slate-800">{calc.nominal_capacity} kWh</b></span>}
          {calc.show_energy && perf.annual_generation > 0 && <span className="text-slate-500">Annual Production: <b className="text-slate-800">{Math.round(perf.annual_generation).toLocaleString()} kWh</b></span>}
          {calc.show_energy && perf.annual_saving > 0 && <span className="text-slate-500">Annual Savings: <b className="text-emerald-700">{currency(perf.annual_saving)}</b></span>}
        </div>
      </div>

      {/* pricing */}
      <div className="qs-avoid-break mt-6 border border-slate-200 rounded-xl p-5">
        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-3">Pricing</div>
        <div className="flex justify-between py-1.5 text-sm"><span className="font-semibold">Total System Price (incl. GST)</span><span className="font-bold">{currency(calc.total_system_price)}</span></div>
        {calc.pv_rebate > 0 && <div className="flex justify-between py-1 text-sm text-emerald-600"><span>STC Panel Incentive ({calc.pv_stcs} STCs)</span><span>− {currency(calc.pv_rebate)}</span></div>}
        {calc.batt_rebate > 0 && <div className="flex justify-between py-1 text-sm text-emerald-600"><span>STC Battery Incentive ({calc.batt_stcs} STCs)</span><span>− {currency(calc.batt_rebate)}</span></div>}
        {calc.is_vic && calc.vic_rebate > 0 && <div className="flex justify-between py-1 text-sm text-emerald-600"><span>Solar VIC Rebate</span><span>− {currency(calc.vic_rebate)}</span></div>}
        {calc.is_vic && calc.vic_loan > 0 && <div className="flex justify-between py-1 text-sm text-emerald-600"><span>Solar VIC Interest-Free Loan</span><span>− {currency(calc.vic_loan)}</span></div>}
        {calc.strategic_discount > 0 && <div className="flex justify-between py-1 text-sm text-emerald-600"><span>Strategic Discount</span><span>− {currency(calc.strategic_discount)}</span></div>}
        <div className="flex items-center justify-between mt-3 rounded-lg bg-emerald-50 border border-emerald-100 px-4 py-3">
          <span className="font-bold text-slate-800">Final Price (incl. GST)</span>
          <span className="text-2xl font-extrabold text-emerald-700">{currency(calc.final_price)}</span>
        </div>
        {calc.deposit > 0 && (
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div className="border border-slate-200 rounded-lg p-3"><div className="text-[10px] uppercase text-slate-400 font-semibold">Deposit</div><div className="font-bold">{formatMoney(calc.deposit)}</div></div>
            <div className="border border-slate-200 rounded-lg p-3"><div className="text-[10px] uppercase text-slate-400 font-semibold">Balance</div><div className="font-bold">{currency(calc.balance)}</div></div>
          </div>
        )}
        {calc.finance && (
          <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 text-sm flex justify-between">
            <span className="font-semibold">{calc.finance.provider_label} Finance — {calc.finance.term_months} mo</span>
            <span className="font-bold text-emerald-700">{currency(calc.finance.monthly_repayment)}/mo</span>
          </div>
        )}
      </div>
      {/* signature — small so the quotation fits one page; flows to page 2 if needed */}
      <div className="qs-avoid-break mt-6 border border-slate-200 rounded-xl p-5">
        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-3">Customer Acceptance</div>
        <div className="grid grid-cols-2 gap-8 items-end">
          <div>
            {quote.signed ? (
              quote.signature_image
                ? <img src={quote.signature_image} alt="signature" className="h-10 object-contain" />
                : <div className="h-10 flex items-end font-semibold italic text-slate-800">{quote.signed_by}</div>
            ) : <div className="h-10" />}
            <div className="border-t border-slate-300 mt-1 pt-1 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Signature{quote.signed_by ? ` — ${quote.signed_by}` : ""}</div>
          </div>
          <div>
            <div className="h-10 flex items-end font-semibold text-slate-800">{quote.signed ? fmtAU(quote.signature_date || quote.signed_at) : ""}</div>
            <div className="border-t border-slate-300 mt-1 pt-1 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Date</div>
          </div>
        </div>
      </div>

      {/* Terms & Conditions — appended pages */}
      {terms && (
        <div className="qs-terms mt-6 pt-5 border-t border-slate-200" data-testid="quotation-terms">
          <h3 className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-2">Terms &amp; Conditions</h3>
          <p className="text-sm text-slate-500 leading-relaxed whitespace-pre-line">{terms}</p>
        </div>
      )}

      <div className="mt-6 text-center text-xs text-slate-400">Thank you for choosing {company.name || "Grian Solar Power"}.</div>
    </div>
  );
}

/* ------------- Reusable component card ------------- */
function ComponentCard({ title, p, qty, icon }) {
  const IconEl = Icons[icon] || Icons.Package;
  return (
    <div className="pq-avoid-break bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-start">
        <div className="sm:w-72 shrink-0 bg-white border-b sm:border-b-0 sm:border-r border-slate-100 flex items-center justify-center p-5 self-stretch">
          <div className="w-full h-56 sm:h-64 flex items-center justify-center" data-testid="product-image-box">
            {p.image_id ? <img src={fileUrl(p.image_id)} alt={p.model_no} className="max-w-full max-h-full w-auto h-auto object-contain" style={{ objectFit: "contain" }} />
              : <IconEl size={44} className="text-slate-300" />}
          </div>
        </div>
        <div className="flex-1 p-6">
          <div className="flex items-start justify-between gap-4">
            <div><div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-emerald-600 font-bold"><IconEl size={14} /> {title}</div>
              <h3 className="font-bold text-xl mt-1.5 text-slate-900 leading-tight">{p.brand}</h3>
              <div className="text-slate-500 text-sm">{p.model_no}</div></div>
            <div className="text-right shrink-0">
              {p.brand_logo_id && <img src={fileUrl(p.brand_logo_id)} alt={p.brand} className="h-8 object-contain ml-auto mb-2" />}
              <span className="inline-block whitespace-nowrap bg-emerald-50 text-emerald-700 text-xs font-bold px-2.5 py-1 rounded-full">{`Qty × ${qty}`}</span></div>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            {p.watts && <Spec l="Power" v={`${p.watts} W`} />}
            {p.usable_capacity && <Spec l="Usable capacity" v={`${p.usable_capacity} kWh`} />}
            {p.manufacturing_warranty && <Spec l="Product warranty" v={p.manufacturing_warranty} />}
            {p.performance_warranty && <Spec l="Performance warranty" v={p.performance_warranty} />}
          </div>
          {(p.features || []).length > 0 && (
            <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
              {p.features.map((f, i) => (<li key={i} className="flex items-start gap-2 text-sm text-slate-600"><Icons.Check size={15} className="text-emerald-500 mt-0.5 shrink-0" /> <span>{f}</span></li>))}
            </ul>
          )}
          {p.description && <p className="mt-4 text-sm text-slate-500 leading-relaxed border-t border-slate-100 pt-3">{p.description}</p>}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4">
            {p.datasheet_id && <a href={`${fileUrl(p.datasheet_id)}?download=1`} download className="inline-flex items-center gap-1.5 text-sm text-emerald-600 font-semibold underline"><Icons.FileDown size={14} /> Download Datasheet</a>}
            {p.warranty_id && <a href={`${fileUrl(p.warranty_id)}?download=1`} download className="inline-flex items-center gap-1.5 text-sm text-emerald-600 font-semibold underline"><Icons.ShieldCheck size={14} /> Warranty Document</a>}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------- Option deep-dive ------------- */
function OptionDetail({ calc, lead, quote, S, exporting, showHeader, idx, onAccept }) {
  const perf = calc.performance;
  const prop = lead?.property_details || {};
  const env = calc.environmental;
  const layout = calc.roof_layout || {};
  const roofImageId = calc.roof_image_id;
  const roofView = calc.roof_view || {};
  const sw = S.system_works || {};
  const isBatteryOnly = calc.quote_type === "battery";
  const extras = (calc.extras || []).filter((e) => e.product);
  const qAddress = lead?.full_address || [lead?.street, lead?.suburb, lead?.state, lead?.postcode].filter(Boolean).join(", ");

  const savePct = perf.annual_bill_before > 0 ? Math.round((perf.annual_saving / perf.annual_bill_before) * 100) : 0;
  const beYear = perf.breakeven_years ? Math.round(perf.breakeven_years) : null;
  const cumData = (perf.yearly || []).map((y) => ({ year: y.year, cumulative: y.cumulative_saving, annual: y.annual_saving }));

  const roofSection = (layout.center || roofImageId) && !isBatteryOnly ? (
    <Section>
      <Kicker>Roof Layout Design</Kicker><H2>Proposed panel placement</H2>
      <div className="mt-6">
      {layout.center ? (
        <div className="relative w-full rounded-2xl border border-slate-100 overflow-hidden shadow-sm" style={{ aspectRatio: "16 / 10" }}>
          <img alt="roof satellite" crossOrigin="anonymous" className="absolute inset-0 w-full h-full object-cover"
            src={`https://maps.googleapis.com/maps/api/staticmap?center=${layout.center.lat},${layout.center.lng}&zoom=${layout.zoom || 20}&size=640x400&scale=2&maptype=satellite&key=${GKEY}`} />
          {(() => {
            const geo = (layout.panels || []).some((p) => p.lat != null);
            if (geo) {
              const cLat = layout.center.lat, cLng = layout.center.lng, z = layout.zoom || 20;
              const mpp = metersPerPx(cLat, z), W = 640, H = 400;
              const lenM = (layout.panelLenMm || 1722) / 1000, widM = (layout.panelWidMm || 1134) / 1000, ang = layout.angle || 0;
              return (layout.panels || []).map((p, i) => {
                const mE = (p.lng - cLng) * 111320 * Math.cos(cLat * Math.PI / 180), mN = (p.lat - cLat) * 110540;
                const xf = 0.5 + (mE / mpp) / W, yf = 0.5 - (mN / mpp) / H, land = p.rot === 90;
                const wf = (land ? lenM : widM) / mpp / W, hf = (land ? widM : lenM) / mpp / H;
                return <div key={i} style={{ left: `${xf * 100}%`, top: `${yf * 100}%`, width: `${wf * 100}%`, height: `${hf * 100}%`, transform: `translate(-50%,-50%) rotate(${ang}deg)` }} className="absolute bg-emerald-500/50 border-2 border-emerald-400 rounded-[1px]" />;
              });
            }
            return (layout.panels || []).map((p, i) => (<div key={i} style={{ left: `${p.xf * 100}%`, top: `${p.yf * 100}%`, width: `${(layout.panelWf || 0.1) * 100}%`, aspectRatio: "1762 / 1134", transform: `translate(-50%,-50%) rotate(${p.rot}deg)` }} className="absolute bg-emerald-500/50 border-2 border-emerald-400 rounded-[1px]" />));
          })()}
        </div>
      ) : (
        <div className="relative w-full rounded-2xl border border-slate-100 overflow-hidden shadow-sm" style={{ aspectRatio: "16/9" }}>
          <img src={fileUrl(roofImageId)} alt="roof" data-testid="roof-image" style={{ "--roof-tf": `translate(${roofView.x || 0}%, ${roofView.y || 0}%) scale(${roofView.zoom || 1})`, transform: `translate(${roofView.x || 0}%, ${roofView.y || 0}%) scale(${roofView.zoom || 1})`, transformOrigin: "center center" }} className="absolute inset-0 w-full h-full object-cover" />
          {(layout.panels || []).map((p, i) => { const w = layout.scale || 70, h = (layout.scale || 70) * (PANEL_MM.h / PANEL_MM.w);
            return <div key={i} style={{ left: p.x, top: p.y, width: p.rot ? h : w, height: p.rot ? w : h }} className="absolute bg-emerald-500/50 border-2 border-emerald-400 rounded-sm" />; })}
        </div>
      )}
      <p className="text-sm text-slate-500 mt-3">{(layout.panels || []).length} panels · to-scale placement.</p>
      </div>
    </Section>
  ) : null;

  return (
    <div>
      {showHeader && (
        <div className="pdf-break pq-page-start pq-block bg-emerald-600 text-white px-6 sm:px-10 py-6">
          <div className="max-w-6xl mx-auto font-extrabold text-2xl sm:text-3xl">System Option — {calc.option_label} <span className="opacity-80 font-medium text-lg">({quote.quote_no}-{calc.option_code})</span></div>
        </div>
      )}

      {/* Design & Solutions */}
      <Split kicker="Design & Solutions" title="A system tailored to your home" img={IMG.house} imgAlt="rooftop solar">
        <div className="grid grid-cols-2 gap-4">
          {!isBatteryOnly && <Metric label="Solar PV Size" value={`${calc.system_kw}`} unit="kW" sub="Solar array" />}
          {(calc.quote_type === "hybrid" || isBatteryOnly) && calc.nominal_capacity > 0 &&
            <Metric label="Battery Storage" value={`${calc.nominal_capacity}`} unit="kWh" sub="Total capacity" />}
          {!isBatteryOnly && <Metric label="Annual Production" value={`${(perf.annual_generation || 0).toLocaleString()}`} unit="kWh" sub="Estimated per year" />}
          {calc.show_energy && <Metric label="1st-Year Savings" value={currency(perf.annual_saving)} green sub={`~${savePct}% off your bill`} />}
          {calc.show_energy && <Metric label="Payback Period" value={perf.breakeven_years ? `${perf.breakeven_years}` : "—"} unit={perf.breakeven_years ? "yrs" : ""} sub="Discounted breakeven" />}
          <motion.div {...popIn} className="col-span-2 pq-avoid-break relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-600 to-emerald-700 text-white p-6 flex items-center justify-between shadow-lg">
            <div className="relative"><div className="text-emerald-100 text-sm font-medium">Your final investment</div>
              <div className="text-3xl sm:text-4xl font-extrabold mt-1">{exporting ? currency(calc.final_price) : <CountUp to={calc.final_price} format={currency} />}</div></div>
            <Icons.Sparkles size={34} className="relative text-emerald-200" />
          </motion.div>
        </div>
      </Split>

      {/* Site details — always shown; blank fields → "Pending from customer" */}
      <Section className="pdf-break">
        <Kicker>Site Details</Kicker><H2>Your property at a glance</H2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-8">
          {[["Property Type", prop.property_type], ["Stories", prop.no_of_stories], ["Roof Type", prop.roof_type],
            ["Roof Pitch", prop.roof_pitch], ["Tilt Req.", prop.tilt_requirement], ["Phase", prop.phase],
            ["Switch Board", prop.switchboard], ["Distributor", prop.distributor]].map(([l, v]) => (
            <div key={l} className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm pq-avoid-break">
              <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{l}</div>
              <div className={`font-semibold text-sm mt-1 ${v ? "text-slate-800" : "text-amber-600 italic"}`}>{v || "Pending from customer"}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Roof layout — its own page, after Design & Solutions + Site Details */}
      {roofSection}

      {/* Components */}
      <Section className="bg-slate-50/70 pdf-break">
        <Kicker>System Components</Kicker><H2>What&apos;s included</H2>
        <div className="mt-8 grid gap-5">
          {[["Solar Panels", calc.panel, calc.panel_qty, "SunMedium"], ["Inverter", calc.inverter, calc.inverter_qty, "Zap"], ["Battery", calc.battery, calc.battery_qty, "BatteryCharging"]]
            .filter(([, p]) => p).map(([title, p, qty, icon]) => (
              <ComponentCard key={title} title={title} p={p} qty={qty} icon={icon} />
            ))}
        </div>
      </Section>

      {/* Extra components — own page, only when the consultant added extras */}
      {extras.length > 0 && (
        <Section className="pdf-break">
          <Kicker>Extra Components</Kicker><H2>Additional equipment included</H2>
          <div className="mt-8 flex flex-wrap gap-3" data-testid="extras-chips">
            {extras.map((e, i) => (
              <div key={i} className="pq-avoid-break bg-white border border-slate-100 rounded-xl shadow-sm px-4 py-3 flex items-center gap-3 min-w-[200px] max-w-[280px]" data-testid={`extra-chip-${idx}-${i}`}>
                <div className="w-12 h-12 shrink-0 flex items-center justify-center bg-white rounded-lg border border-slate-100">
                  {e.product?.image_id ? <img src={fileUrl(e.product.image_id)} alt={e.product.model_no} className="max-w-full max-h-full object-contain" /> : <Icons.Cpu size={20} className="text-emerald-500" />}
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-sm text-slate-900 leading-tight truncate">{e.product?.brand} {e.product?.model_no}</div>
                  <div className="text-[11px] text-slate-500 truncate">{e.product?.category === "gateway" ? "Gateway / EMS" : (e.product?.category === "meter" ? "Smart meter" : (e.product?.category || "Component"))} · Qty {e.qty || 1}</div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Energy insights — only for PV / hybrid */}
      {calc.show_energy && (
        <Section className="bg-slate-50/60 pdf-break">
          <Kicker>Energy Insights</Kicker><H2>Your estimated system performance</H2>
          <div className="mt-8 bg-white border border-slate-100 rounded-2xl p-5 sm:p-7 pq-avoid-break shadow-sm">
            <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-4">Monthly Energy — Generation vs Consumption (kWh)</div>
            <ResponsiveContainer width="100%" height={exporting ? 280 : 330}>
              <BarChart data={calc.monthly} barGap={3}>
                <defs>
                  <linearGradient id="barGen" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#34d399" /><stop offset="100%" stopColor="#059669" /></linearGradient>
                  <linearGradient id="barCons" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#e5eaf0" /><stop offset="100%" stopColor="#c2ccd8" /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef1f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "rgba(16,185,129,0.06)" }} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
                <Legend />
                <Bar dataKey="generation" name="Solar Generation" fill="url(#barGen)" radius={[7, 7, 0, 0]} maxBarSize={26} isAnimationActive={!exporting} />
                <Bar dataKey="consumption" name="Consumption" fill="url(#barCons)" radius={[7, 7, 0, 0]} maxBarSize={26} isAnimationActive={!exporting} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className={`mt-6 grid gap-6 ${exporting ? "grid-cols-2" : "lg:grid-cols-2"}`}>
            {/* Annual savings bar */}
            <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm pq-avoid-break">
              <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-1">Annual Savings (25 years)</div>
              <div className="text-2xl font-extrabold text-emerald-600 mb-2">{currency(perf.annual_saving)}<span className="text-sm text-slate-400 font-semibold"> in year 1</span></div>
              <ResponsiveContainer width="100%" height={exporting ? 190 : 220}>
                <BarChart data={cumData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef1f0" />
                  <XAxis dataKey="year" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} ticks={[1, 5, 10, 15, 20, 25]} />
                  <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
                  <Tooltip formatter={(v) => currency(v)} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
                  <Bar dataKey="annual" name="Annual saving" fill="#10b981" radius={[5, 5, 0, 0]} maxBarSize={16} isAnimationActive={!exporting} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            {/* Cumulative savings area */}
            <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm pq-avoid-break">
              <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-1">25-Year Cumulative Savings</div>
              <div className="text-2xl font-extrabold text-slate-900 mb-2">{currency(perf.savings_25yr)}<span className="text-sm text-slate-400 font-semibold"> lifetime</span></div>
              <ResponsiveContainer width="100%" height={exporting ? 190 : 220}>
                <AreaChart data={cumData} margin={{ left: -6, right: 6 }}>
                  <defs><linearGradient id="cumSave" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10b981" stopOpacity={0.4} /><stop offset="100%" stopColor="#10b981" stopOpacity={0.04} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef1f0" />
                  <XAxis dataKey="year" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} ticks={[1, 5, 10, 15, 20, 25]} />
                  <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
                  <Tooltip formatter={(v) => currency(v)} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
                  <Area type="monotone" dataKey="cumulative" name="Cumulative savings" stroke="#10b981" fill="url(#cumSave)" strokeWidth={2} isAnimationActive={!exporting} />
                  {beYear && <ReferenceDot x={beYear} y={cumData[beYear - 1]?.cumulative} r={5} fill="#10b981" stroke="#fff" strokeWidth={2} />}
                </AreaChart>
              </ResponsiveContainer>
              {beYear && <p className="text-xs text-slate-500 mt-1"><b className="text-emerald-600">Breakeven at {perf.breakeven_years} years</b> — after that, pure savings.</p>}
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-4 leading-relaxed max-w-4xl">{S.energy_disclaimer}</p>
        </Section>
      )}

      {/* How the system works — all 3 phases stacked */}
      {!isBatteryOnly && (
        <Section className="pdf-break">
          <Kicker>How the System Works</Kicker><H2>Clean power, around the clock</H2>
          <div className="mt-8 grid md:grid-cols-3 gap-5">
            {["morning", "afternoon", "night"].map((phase) => (
              <div key={phase} className="bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-sm pq-avoid-break">
                <PhaseScene phase={phase} hasBattery={!!calc.battery} />
                <div className="px-5 py-4">
                  <div className="font-bold text-slate-800 capitalize mb-1">{phase}</div>
                  <p className="text-sm text-slate-500 leading-relaxed">{sw[phase]}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Environmental */}
      {!isBatteryOnly && (
        <Split kicker="Environmental Benefits" title="Every year, your system offsets" img={IMG.leaves} imgAlt="green leaves" reverse>
          <div className="grid grid-cols-1 gap-4">
            <div className="rounded-2xl bg-emerald-600 text-white p-6 pq-avoid-break">
              <Icons.Leaf size={28} className="text-emerald-200" />
              <div className="text-4xl font-extrabold mt-2">{env.co2_offset_kg.toLocaleString()} kg</div>
              <div className="text-emerald-100">CO₂ emissions avoided per year</div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <EnvCard icon="Trees" value={env.trees_equivalent.toLocaleString()} label="trees planted" />
              <EnvCard icon="Fuel" value={Math.round(env.co2_offset_kg / 2.31).toLocaleString()} label="litres of petrol" />
              <EnvCard icon="Car" value={Math.round(env.co2_offset_kg / 0.192).toLocaleString()} label="km not driven" />
            </div>
          </div>
        </Split>
      )}

      {/* Quotation — full property quotation (customer + site details + system + pricing) */}
      <Section className="pdf-break pq-page-start">
        <Kicker>Quotation</Kicker>
        <H2>Quotation — {quote.quote_no}-{calc.option_code}</H2>
        {calc.expires_on && <p className="mt-2 text-sm text-slate-600" data-testid={`quote-expiry-${calc.option_id}`}>Valid until {calc.expires_on.split("-").reverse().join("/")}</p>}
        <div className="mt-8 grid lg:grid-cols-2 gap-6 items-start">
          {/* Left — customer, address, site details, system summary */}
          <div className="space-y-5">
            <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm pq-avoid-break">
              <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Prepared for</div>
              <div className="font-bold text-lg text-slate-900">{lead?.first_name} {lead?.last_name}</div>
              {qAddress && <div className="text-sm text-slate-500 mt-1 flex items-start gap-1.5"><Icons.MapPin size={14} className="mt-0.5 shrink-0" /> {qAddress}</div>}
              {lead?.email && <div className="text-sm text-slate-500 mt-1 flex items-center gap-1.5" data-testid="prepared-for-email"><Icons.Mail size={14} className="shrink-0" /> {lead.email}</div>}
              {lead?.phone && <div className="text-sm text-slate-500 mt-0.5 flex items-center gap-1.5" data-testid="prepared-for-phone"><Icons.Phone size={14} className="shrink-0" /> {lead.phone}</div>}
            </div>
            <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm pq-avoid-break">
              <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-3">Site Details</div>
              <div className="grid grid-cols-2 gap-3">
                {siteDetailPairs(lead).map(([l, v]) => (
                  <div key={l}><div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{l}</div>
                    <div className={`text-sm font-semibold ${v ? "text-slate-800" : "text-amber-600 italic"}`}>{v || "Pending"}</div></div>
                ))}
              </div>
            </div>
            <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm pq-avoid-break">
              <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-3">System Summary</div>
              <ul className="space-y-2">
                {productSummary(calc).map((p, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><Icons.Check size={15} className="text-emerald-500 mt-0.5 shrink-0" />
                    <span><b>{`${p.qty}× ${p.brand}`}</b> {p.model}{p.meta ? <span className="text-slate-400"> · {p.meta}</span> : null}</span></li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 pt-3 border-t border-slate-100 text-sm">
                {!isBatteryOnly && calc.system_kw > 0 && <span className="text-slate-500">Solar PV: <b className="text-slate-800">{calc.system_kw} kW</b></span>}
                {calc.nominal_capacity > 0 && <span className="text-slate-500">Battery: <b className="text-slate-800">{calc.nominal_capacity} kWh</b></span>}
                {calc.show_energy && perf.annual_generation > 0 && <span className="text-slate-500">Annual Production: <b className="text-slate-800">{Math.round(perf.annual_generation).toLocaleString()} kWh</b></span>}
                {calc.show_energy && perf.annual_saving > 0 && <span className="text-slate-500">Annual Savings: <b className="text-emerald-700">{currency(perf.annual_saving)}</b></span>}
              </div>
            </div>
          </div>
          {/* Right — pricing */}
          <div className="bg-slate-50 border border-slate-100 rounded-2xl p-6 sm:p-7 pq-avoid-break">
            <h3 className="text-xl font-bold text-slate-900 mb-4">{calc.option_label}</h3>
            <PriceRow l="Total System Price (incl. GST)" v={currency(calc.total_system_price)} />
            {calc.total_rebates > 0 && (
              <div className="pl-3 border-l-2 border-emerald-200 ml-1 my-2 space-y-1.5 text-sm text-emerald-600/90">
                {calc.pv_rebate > 0 && <PriceMini l={`STC Panel Incentive (${calc.pv_stcs} STCs)`} v={`− ${currency(calc.pv_rebate)}`} green />}
                {calc.batt_rebate > 0 && <PriceMini l={`STC Battery Incentive (${calc.batt_stcs} STCs)`} v={`− ${currency(calc.batt_rebate)}`} green />}
                {calc.is_vic && calc.vic_rebate > 0 && <PriceMini l="Solar VIC Rebate" v={`− ${currency(calc.vic_rebate)}`} green />}
                {calc.is_vic && calc.vic_loan > 0 && <PriceMini l="Solar VIC Interest-Free Loan" v={`− ${currency(calc.vic_loan)}`} green />}
              </div>
            )}
            {calc.strategic_discount > 0 && <PriceRow l="Strategic Discount" v={`− ${currency(calc.strategic_discount)}`} green />}
            <div className="mt-4 rounded-xl bg-white border border-slate-200 p-5 flex items-center justify-between">
              <div><div className="text-sm text-slate-500">Final Price <span className="text-xs text-slate-400">(incl. GST)</span></div>
                <div className="text-3xl sm:text-4xl font-extrabold text-slate-900 mt-0.5">{currency(calc.final_price)}</div></div>
              <Icons.BadgeCheck size={40} className="text-emerald-500" />
            </div>
            {calc.deposit > 0 && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div className="bg-white border border-slate-200 rounded-xl p-4"><div className="text-xs text-slate-400">Deposit</div><div className="text-lg font-bold text-slate-800">{formatMoney(calc.deposit)}</div></div>
                <div className="bg-white border border-slate-200 rounded-xl p-4"><div className="text-xs text-slate-400">Balance</div><div className="text-lg font-bold text-slate-800">{currency(calc.balance)}</div></div>
              </div>
            )}
            {calc.finance && (
              <div className="mt-4 rounded-xl bg-emerald-50 border border-emerald-100 p-5" data-testid="finance-block">
                <div className="flex items-center gap-2 text-emerald-800 font-bold text-sm mb-3"><Icons.Landmark size={16} /> {calc.finance.provider_label} Finance — {calc.finance.term_months} months</div>
                <div className="grid grid-cols-2 gap-y-2 text-sm">
                  <span className="text-slate-500">Amount financed</span><span className="text-right font-semibold">{currency(calc.finance.financed)}</span>
                  <span className="text-slate-700 font-semibold">Estimated monthly</span><span className="text-right font-extrabold text-emerald-700">{currency(calc.finance.monthly_repayment)}/mo</span>
                </div>
              </div>
            )}
            {calc.option_notes && (
              <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm text-slate-700 leading-relaxed flex gap-2.5" data-testid="option-notes-pricing">
                <Icons.Info size={16} className="text-emerald-600 shrink-0 mt-0.5" />
                <span className="whitespace-pre-line">{calc.option_notes}</span>
              </div>
            )}
          </div>
        </div>
      </Section>

      {/* Acceptance — signature directly below the pricing for this option */}
      <div className="pq-block max-w-6xl mx-auto px-6 sm:px-10 pb-16 sm:pb-20 -mt-4 sm:-mt-8">
        <div className="flex items-center gap-2.5 mb-4"><span className="h-px w-8 bg-emerald-500/70" /><span className="text-xs uppercase tracking-[0.25em] font-semibold text-emerald-600">Acceptance — {calc.option_label}</span></div>
        <SignatureBlock quote={quote} optionLabel={calc.option_label} onOpen={onAccept} testId={`price-signature-${idx}`} />
      </div>
    </div>
  );
}

/* ------------- Accept modal ------------- */
function AcceptModal({ token, options, defaultSel, S, onClose, onSigned }) {
  const [chosen, setChosen] = useState(options[defaultSel]?.option_id || options[0].option_id);
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const sigRef = useRef();
  const today = new Date().toLocaleDateString("en-AU", { day: "2-digit", month: "long", year: "numeric" });

  const submit = async () => {
    if (!name.trim()) return toast.error("Please type your full name");
    if (!agree) return toast.error("Please accept the terms & conditions");
    if (!sigRef.current || sigRef.current.isEmpty()) return toast.error("Please draw your signature in the box");
    let sigImg = null;
    try { sigImg = sigRef.current.getTrimmedCanvas().toDataURL("image/png"); }
    catch { try { sigImg = sigRef.current.getCanvas().toDataURL("image/png"); } catch { /* noop */ } }
    if (!sigImg) return toast.error("Could not read your signature — please clear and sign again");
    setBusy(true);
    try {
      const { data: receipt } = await axios.post(`${API}/public/quote/${token}/sign`, {
        signature_name: name, signature_image: sigImg, signature_date: new Date().toISOString(),
        selected_option_id: chosen, terms_accepted: true, terms_version: S.terms_version,
      });
      toast.success("Proposal accepted & signed!");
      onSigned(receipt);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to sign"); } finally { setBusy(false); }
  };

  return (
    <div className="no-print fixed inset-0 z-[70] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="accept-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full my-8 shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="font-bold text-lg text-slate-900">Accept your proposal</h3>
          <button onClick={onClose} data-testid="accept-close" className="text-slate-400 hover:text-slate-700"><Icons.X size={20} /></button>
        </div>
        <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* choose option */}
          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-slate-400 mb-2">1 · Choose your system</div>
            <div className="space-y-2">
              {options.map((o, i) => (
                <label key={o.option_id} data-testid={`accept-option-${i}`}
                  className={`flex items-center justify-between gap-3 border-2 rounded-xl px-4 py-3 cursor-pointer transition-colors ${chosen === o.option_id ? "border-emerald-500 bg-emerald-50" : "border-slate-200"}`}>
                  <span className="flex items-center gap-2.5">
                    <input type="radio" name="opt" checked={chosen === o.option_id} onChange={() => setChosen(o.option_id)} className="accent-emerald-600" />
                    <span className="font-semibold text-slate-800 text-sm">{o.option_label}</span>
                  </span>
                  <span className="font-extrabold text-slate-900">{currency(o.final_price)}</span>
                </label>
              ))}
            </div>
          </div>
          {/* terms */}
          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-slate-400 mb-2">2 · Terms &amp; Conditions</div>
            <div className="text-xs text-slate-500 leading-relaxed max-h-28 overflow-y-auto border border-slate-200 rounded-lg p-3 whitespace-pre-line">{S.terms_and_conditions}</div>
            <label className="flex items-start gap-2 mt-2 text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="accept-terms" className="mt-0.5 accent-emerald-600" />
              I have read and accept the terms &amp; conditions.
            </label>
          </div>
          {/* signature */}
          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-slate-400 mb-2">3 · Sign below</div>
            <label className="text-xs font-semibold text-slate-500">Full name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Type your full name" data-testid="accept-name"
              className="w-full mt-1 mb-3 px-4 py-2.5 rounded-lg border-2 border-slate-200 focus:border-emerald-500 outline-none" />
            <div className="border-2 border-slate-200 rounded-xl overflow-hidden bg-slate-50">
              <CustomerSignaturePad ref={sigRef} />
            </div>
            <div className="flex items-center justify-between mt-1">
              <button onClick={() => sigRef.current?.clear()} data-testid="signature-clear" className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1"><Icons.Eraser size={13} /> Clear</button>
              <span className="text-xs text-slate-400">Draw with your finger or mouse</span>
            </div>
            <div className="text-xs text-slate-400 mt-1.5 flex items-center gap-1"><Icons.Calendar size={12} /> Date: {today}</div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-100">
          <button onClick={submit} disabled={busy || !name.trim() || !agree} data-testid="accept-submit"
            className="w-full px-6 py-3 rounded-full bg-emerald-600 text-white font-semibold hover:bg-emerald-700 transition-all disabled:opacity-50">
            {busy ? "Submitting…" : "Accept & Sign Proposal"}</button>
        </div>
      </div>
    </div>
  );
}

/* ------------- Summary card ------------- */
const summarySystem = (c) => {
  const parts = [];
  if (c.system_kw > 0) parts.push(`${c.system_kw} kW`);
  if (c.nominal_capacity > 0) parts.push(`${c.nominal_capacity} kWh battery`);
  return parts.join(" + ");
};
const SummaryCard = ({ c, idx, active, onSelect }) => (
  <motion.div {...popIn} onClick={onSelect} data-testid={`summary-card-${idx}`}
    className={`relative cursor-pointer pq-avoid-break rounded-2xl border-2 p-5 bg-white transition-all ${c.recommended ? "border-emerald-500 shadow-lg ring-2 ring-emerald-200" : active ? "border-emerald-500 shadow-lg" : "border-slate-100 shadow-sm hover:border-emerald-200"}`}>
    {c.recommended && (
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-[11px] font-bold px-3 py-1 rounded-full flex items-center gap-1 whitespace-nowrap" data-testid={`recommended-badge-${idx}`}>
        <Icons.Star size={12} className="fill-white" /> RECOMMENDED
      </div>
    )}
    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mt-1">Option {idx + 1}</div>
    <div className="font-bold text-lg text-slate-900 mt-0.5">{c.option_label}</div>
    <div className="text-sm font-semibold text-emerald-700 mt-1">{summarySystem(c) || c.quote_type}</div>
    <div className="my-4 space-y-2 text-sm border-t border-slate-100 pt-3">
      {c.panel && <SumLine icon="SunMedium" label="Panels" v={`${c.panel.brand} ${c.panel.model_no}`} qty={c.panel_qty} />}
      {c.battery && <SumLine icon="BatteryCharging" label="Battery" v={`${c.battery.brand} ${c.battery.model_no}`} qty={c.battery_qty} />}
      {c.inverter && <SumLine icon="Zap" label="Inverter" v={`${c.inverter.brand} ${c.inverter.model_no}`} qty={c.inverter_qty} />}
    </div>
    <div className="text-3xl font-extrabold text-slate-900">{currency(c.final_price)}</div>
    <div className="text-xs text-slate-500">out of pocket</div>
    <button className={`mt-4 w-full py-2 rounded-full text-sm font-semibold transition-colors ${active ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
      {active ? "Viewing" : "View details"}</button>
  </motion.div>
);
const SumLine = ({ icon, label, v, qty }) => {
  const Ic = Icons[icon] || Icons.Package;
  return (
    <div className="flex items-start gap-2">
      <Ic size={15} className="text-emerald-500 mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}{qty ? ` · ×${qty}` : ""}</div>
        <div className="text-sm text-slate-700 font-medium leading-tight">{v}</div>
      </div>
    </div>
  );
};

/* ------------- how-it-works static scene ------------- */
const PHASE_CFG = {
  morning: { sky1: "#fcd9a6", sky2: "#f6b45f", ground: "#3a2f28" },
  afternoon: { sky1: "#bfe4ff", sky2: "#7cc0f5", ground: "#2f3b30" },
  night: { sky1: "#1e2a4a", sky2: "#0f1830", ground: "#0b1020" },
};
function PhaseScene({ phase, hasBattery }) {
  const p = PHASE_CFG[phase]; const isNight = phase === "night";
  const flows = { morning: [{ d: "M235,150 L215,205", c: "#10b981" }, hasBattery && { d: "M250,158 L292,225", c: "#34d399" }],
    afternoon: [{ d: "M235,150 L215,205", c: "#10b981" }, { d: "M270,150 L470,175", c: "#10b981" }],
    night: [hasBattery && { d: "M300,235 L235,215", c: "#34d399" }, { d: "M470,178 L245,215", c: "#f59e0b" }] }[phase].filter(Boolean);
  return (
    <svg viewBox="0 0 600 300" className="w-full block">
      <defs><linearGradient id={`sky-${phase}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={p.sky1} /><stop offset="100%" stopColor={p.sky2} /></linearGradient></defs>
      <rect x="0" y="0" width="600" height="230" fill={`url(#sky-${phase})`} />
      <rect x="0" y="230" width="600" height="70" fill={p.ground} />
      {isNight ? <g><circle cx="120" cy="70" r="26" fill="#e8edff" /><circle cx="112" cy="62" r="26" fill={p.sky1} /></g>
        : <circle cx={phase === "morning" ? 110 : 300} cy={phase === "morning" ? 120 : 70} r="30" fill="#fff3c4" opacity="0.95" />}
      <g><polygon points="150,140 250,90 350,140" fill="#e2e8f0" /><rect x="165" y="140" width="170" height="90" fill="#f1f5f9" />
        <rect x="200" y="180" width="30" height="50" fill={isNight ? "#fcd34d" : "#cbd5e1"} />
        <g transform="translate(196,96) skewY(-27)">{[0, 1, 2].map((r) => [0, 1, 2, 3].map((c) => (<rect key={`${r}-${c}`} x={c * 20} y={r * 15} width="17" height="12" rx="1" fill="#1e293b" stroke="#38bdf8" strokeWidth="0.6" />)))}</g></g>
      {hasBattery && <rect x="360" y="205" width="26" height="34" rx="3" fill="#10b981" />}
      <g stroke="#94a3b8" strokeWidth="3" fill="none"><line x1="475" y1="150" x2="475" y2="250" /><line x1="455" y1="165" x2="495" y2="165" /><line x1="458" y1="178" x2="492" y2="178" /></g>
      {flows.map((f, i) => (<g key={i}><path d={f.d} stroke={f.c} strokeWidth="2" fill="none" strokeDasharray="4 5" opacity="0.5" />
        {[0, 1, 2].map((k) => (<circle key={k} r="3.5" fill={f.c}><animateMotion dur="1.8s" begin={`${k * 0.6}s`} repeatCount="indefinite" path={f.d} /></circle>))}</g>))}
      <text x="250" y="255" textAnchor="middle" fontSize="11" fill={isNight ? "#cbd5e1" : "#475569"} fontWeight="600">Home</text>
      <text x="475" y="270" textAnchor="middle" fontSize="11" fill={isNight ? "#cbd5e1" : "#475569"} fontWeight="600">Grid</text>
    </svg>
  );
}

/* ------------- helpers ------------- */
const siteDetailPairs = (lead) => {
  const p = lead?.property_details || {};
  return [["Property Type", p.property_type], ["Stories", p.no_of_stories], ["Roof Type", p.roof_type],
    ["Roof Pitch", p.roof_pitch], ["Tilt Req.", p.tilt_requirement], ["Phase", p.phase],
    ["Switch Board", p.switchboard], ["Distributor", p.distributor], ["Retailer", p.retailer],
    ["NMI", p.nmi_no], ["Meter No", p.meter_no], ["Internet", p.internet]];
};
const productSummary = (calc) => {
  const out = [];
  if (calc.panel && calc.panel_qty) out.push({ qty: calc.panel_qty, brand: calc.panel.brand, model: calc.panel.model_no, meta: calc.panel.watts ? `${calc.panel.watts}W panels` : "" });
  if (calc.inverter) out.push({ qty: calc.inverter_qty || 1, brand: calc.inverter.brand, model: calc.inverter.model_no, meta: calc.inverter.size_kw ? `${calc.inverter.size_kw}kW inverter` : "inverter" });
  if (calc.battery) out.push({ qty: calc.battery_qty || 1, brand: calc.battery.brand, model: calc.battery.model_no, meta: calc.battery.usable_capacity ? `${calc.battery.usable_capacity}kWh usable` : "battery" });
  (calc.extras || []).forEach((e) => { if (e.product) out.push({ qty: e.qty || 1, brand: e.product.brand, model: e.product.model_no, meta: "" }); });
  return out;
};
const Stars = ({ n }) => (
  <div className="flex gap-0.5">{[1, 2, 3, 4, 5].map((i) => (<Icons.Star key={i} size={14} className={i <= Math.round(n) ? "text-amber-400 fill-amber-400" : "text-slate-200"} />))}</div>
);
const Section = ({ children, className = "", ...props }) => (
  <motion.section initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.12 }}
    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} className={`pq-section ${className}`} {...props}>
    <div className="pq-wrap max-w-6xl mx-auto px-6 sm:px-10 py-16 sm:py-20">{children}</div>
  </motion.section>
);
const Split = ({ kicker, title, img, imgAlt, children, reverse, plain, breakBefore }) => (
  <motion.section initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.12 }}
    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} className={`pq-section ${breakBefore ? "pdf-break" : ""}`}>
    <div className={`pq-wrap pq-split-grid max-w-6xl mx-auto px-6 sm:px-10 py-16 sm:py-20 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center ${reverse ? "pq-split-reverse lg:[&>*:first-child]:order-2" : ""}`}>
      <div>
        <div className="flex items-center gap-2.5 mb-3"><span className="h-px w-8 bg-emerald-500/70" /><span className="text-xs uppercase tracking-[0.25em] font-semibold text-emerald-600">{kicker}</span></div>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.08]">{title}</h2>
        {img && (<div className="relative mt-8 rounded-3xl overflow-hidden hidden sm:block" style={{ aspectRatio: plain ? "4/3" : "16/11" }}>
          <img src={img} alt={imgAlt} crossOrigin="anonymous" className="w-full h-full object-cover opacity-90" /><div className="absolute inset-0 bg-gradient-to-tr from-white/70 via-white/10 to-transparent" /></div>)}
      </div>
      <div>{children}</div>
    </div>
  </motion.section>
);
const Kicker = ({ children }) => (
  <div className="flex items-center gap-2.5 mb-3"><span className="h-px w-8 bg-emerald-500/70" /><span className="text-xs uppercase tracking-[0.25em] font-semibold text-emerald-600">{children}</span></div>
);
const H2 = ({ children }) => <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 leading-[1.1]">{children}</h2>;
const Spec = ({ l, v }) => (
  <div className="bg-slate-50 border border-slate-100 rounded-lg px-3 py-1.5"><div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{l}</div><div className="text-sm font-bold text-slate-800">{v}</div></div>
);
const CountUp = ({ to, format }) => {
  const [v, setV] = useState(0);
  useEffect(() => { const c = animate(0, Number(to) || 0, { duration: 1.3, ease: "easeOut", onUpdate: (x) => setV(x) }); return () => c.stop(); }, [to]);
  return <>{format ? format(v) : Math.round(v).toLocaleString()}</>;
};
const popIn = { initial: { opacity: 0, scale: 0.9, y: 14 }, whileInView: { opacity: 1, scale: 1, y: 0 }, viewport: { once: true, amount: 0.4 }, transition: { type: "spring", stiffness: 240, damping: 17 } };
const Metric = ({ label, value, unit, sub, green }) => (
  <motion.div {...popIn} whileHover={{ y: -4 }} className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm hover:shadow-lg hover:border-emerald-100 transition-[box-shadow,border-color] duration-300 pq-avoid-break">
    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
    <div className={`text-2xl sm:text-3xl font-extrabold mt-1 ${green ? "text-emerald-600" : "text-slate-900"}`}>{value}<span className="text-base font-bold ml-1 text-slate-400">{unit}</span></div>
    {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
  </motion.div>
);
const EnvCard = ({ icon, value, label }) => {
  const Ic = Icons[icon] || Icons.Leaf;
  return (<motion.div {...popIn} whileHover={{ y: -4 }} className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm hover:shadow-lg transition-shadow duration-300 text-center pq-avoid-break">
    <Ic size={22} className="text-emerald-500 mx-auto mb-2" /><div className="text-2xl font-extrabold text-slate-900">{value}</div><div className="text-xs text-slate-500 mt-0.5">{label}</div></motion.div>);
};
const PriceRow = ({ l, v, green }) => (
  <div className="flex items-center justify-between py-2 border-t border-slate-200 first:border-t-0"><span className="font-semibold text-slate-900">{l}</span><span className={`font-bold ${green ? "text-emerald-600" : "text-slate-900"}`}>{v}</span></div>
);
const PriceMini = ({ l, v, green }) => (
  <div className="flex items-center justify-between"><span>{l}</span>{v && <span className={green ? "text-emerald-600 font-medium" : ""}>{v}</span>}</div>
);
